import { Effect, Schema } from "effect";
import { ApiError, type StaffApi } from "#api.ts";
import { Order, orderMutationSchema } from "#orders.ts";

/** Operational changes only: no bulk payment/refund action. */
export const bulkStatusSchema = Schema.Literal(
  "order_paid_and_in_progress",
  "order_ready",
  "order_completed",
  "order_canceled",
);

/** Each selected snapshot has an independent, revision-checked outcome. */
export type BulkStatusResult =
  | {
      readonly _tag: "Updated";
      readonly before: Order;
      readonly saved: NonNullable<typeof orderMutationSchema.Type>;
    }
  | { readonly _tag: "Unchanged"; readonly before: Order }
  | {
      readonly _tag: "Failed";
      readonly before: Order;
      readonly error: ApiError;
    };

/** Reuse the authenticated single-order API, bounded to one page and three concurrent writes; never replay writes. */
export function changeOrderStatuses(
  api: StaffApi,
  input: {
    readonly orders: readonly Order[];
    readonly status: typeof bulkStatusSchema.Type;
  },
): Effect.Effect<readonly BulkStatusResult[], ApiError> {
  if (input.orders.length === 0 || input.orders.length > 100)
    return Effect.fail(new ApiError(400, "Select between 1 and 100 orders."));
  return Effect.forEach(
    input.orders,
    (before) => {
      if (before.state === input.status)
        return Effect.succeed<BulkStatusResult>({ _tag: "Unchanged", before });
      return api
        .requestEffect({
          path: `/merchants/${encodeURIComponent(before.merchantId)}/orders/${encodeURIComponent(before.id)}`,
          method: "PUT",
          body: {
            order_state: input.status,
            expected_revision: before.revision ?? "unversioned",
          },
          schema: orderMutationSchema,
        })
        .pipe(
          Effect.flatMap((saved) =>
            saved
              ? Effect.succeed(saved)
              : Effect.fail(
                  new ApiError(404, "Order update could not be confirmed."),
                ),
          ),
          Effect.match({
            onSuccess: (saved): BulkStatusResult => ({
              _tag: "Updated",
              before,
              saved,
            }),
            onFailure: (error): BulkStatusResult => ({
              _tag: "Failed",
              before,
              error,
            }),
          }),
        );
    },
    { concurrency: 3 },
  );
}
